/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ header / cell は React component ではなく
 * テーブルの render 関数 (AdminUploadHistory / AdminOcctoMaster の各 Table と同判断)。
 * MasterDataTable は戻り値を <TableCell> の children として表示するだけで、 独立した
 * component subtree を持たないため、 lint 警告の前提 (= subtree 破棄) に該当しない。
 */
import type { JepxSettlement } from '@ebalance/proto/bffuser/v1/jepx_settlement_model_pb'
import { useTranslation } from 'react-i18next'

import { HStack, Text } from '@enechain/ecloud-designsystem'

import { HelpIcon } from '~/components/common/HelpIcon'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { SettlementDetailButton } from '~/pages/JepxSettlement/components/SettlementDetailButton'
import { SettlementRowActions } from '~/pages/JepxSettlement/components/SettlementRowActions'
import { toSettlementAmountView } from '~/pages/JepxSettlement/models/settlementAmount'
import { toSettlementDetailLanding } from '~/pages/JepxSettlement/models/settlementDetail'
import { formatSettlementSubject } from '~/pages/JepxSettlement/models/settlementSubject'

type Props = {
  rows: JepxSettlement[]
  isLoading: boolean
}

const rowKey = (row: JepxSettlement): string =>
  `${row.market}:${row.settlementNumber}`

/**
 * 清算書の一覧 (AC-JST-001)。 列は 清算日・件名・清算額 の 3 つと、 見出し文言の無い行の操作列
 * (「詳細」 AC-JST-009、 「PDF」「CSV」 AC-JST-006・007。 この順で右端に置く)。
 * 件名に間接送電権・定額制を含む行は「詳細」を出さない (AC-JST-010)。 「PDF」「CSV」は全行に出す。
 *
 * - 並びは backend が清算日の新しい順に並べて返したまま出す (ここで並べ替えない)。
 * - 件名は JEPX の原文を出す。 英語表示の時だけ既知の 6 種を英訳する (`models/settlementSubject.ts`)。
 *   既知でない件名は英語表示でも原文のまま。 行データ (`title`) は原文のまま保つ。
 * - 清算額は絶対値で出し、 支払・受取は金額の左に文字で示す (`models/settlementAmount.ts`)。
 * - 件数が多い時はページ送りにせず、 `MasterDataTable` の見出し固定 (sticky) の縦スクロールに任せる
 *   (Des 判断。 backend は全件を返す)。
 */
export const SettlementTable = ({
  rows,
  isLoading,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 件名の英訳は英語表示の時だけ (前例 `pages/Supply48Data/index.tsx` の `i18n.language.startsWith('en')`)。
  const isEnglish = i18n.language.startsWith('en')

  const columns: Array<MasterDataTableColumn<JepxSettlement>> = [
    {
      key: 'settlementDate',
      header: t('page:jepxSettlement.column.settlementDate'),
      nowrap: true,
      cell: (row) =>
        row.settlementDate !== undefined
          ? formatDateDisplay(fromProtoDate(row.settlementDate))
          : '',
    },
    {
      key: 'title',
      header: (
        <HStack gap={1}>
          <Text as="span">{t('page:jepxSettlement.column.title')}</Text>
          <HelpIcon
            ariaLabel={t('page:jepxSettlement.column.titleNote')}
            content={t('page:jepxSettlement.column.titleNote')}
          />
        </HStack>
      ),
      cell: (row) => formatSettlementSubject(row.title, isEnglish, t),
    },
    {
      key: 'amount',
      header: t('page:jepxSettlement.column.amount'),
      format: 'number',
      nowrap: true,
      cell: (row) => {
        const { direction, absoluteYen } = toSettlementAmountView(row.amountYen)
        return (
          // 支払・受取は金額より一段小さい sm、 金額との間は 8px (8080
          // `bidding/settlement.html` の `.stl-dir`)。
          <HStack justifyContent="flex-end" gap={2}>
            {direction !== 'none' && (
              <Text as="span" color="Text.midEmphasis" fontSize="sm">
                {t(`page:jepxSettlement.amount.${direction}`)}
              </Text>
            )}
            <Text as="span">
              {t('page:jepxSettlement.amount.yen', {
                amount: absoluteYen.toLocaleString('ja-JP'),
              })}
            </Text>
          </HStack>
        )
      },
    },
  ]

  return (
    <MasterDataTable
      actionColWidth="280px"
      columns={columns}
      getRowKey={rowKey}
      isLoading={isLoading}
      renderRowActions={(row) => {
        const landing = toSettlementDetailLanding(row)
        return (
          <>
            {landing !== undefined && (
              <SettlementDetailButton landing={landing} />
            )}
            <SettlementRowActions row={row} />
          </>
        )
      }}
      rows={rows}
    />
  )
}
